// Collection value loader; fetches and formats the Discogs min/median/max values.
import { getDiscogsCollectionValue } from '../../api/collection'

/** Loads the collection value summary into the rendered value container. */
export async function loadCollectionValue(
	collectionValueEl: HTMLElement | null,
): Promise<void> {
	if (!collectionValueEl) return

	collectionValueEl.hidden = false
	collectionValueEl.classList.add('is-loading')
	collectionValueEl.setAttribute('aria-busy', 'true')

	try {
		const value = await getDiscogsCollectionValue()
		for (const key of ['minimum', 'median', 'maximum'] as const) {
			const valueEl = collectionValueEl.querySelector<HTMLElement>(
				`[data-collection-value="${key}"]`,
			)
			if (valueEl) {
				const formattedValue = compactCollectionValue(value[key])
				const amountIndex = formattedValue.search(/\d/)
				if (amountIndex > 0) {
					const currencyEl = document.createElement('span')
					currencyEl.className = 'collection-value-currency'
					currencyEl.textContent = formattedValue
						.slice(0, amountIndex)
						.trimEnd()
					valueEl.replaceChildren(
						currencyEl,
						' ',
						formattedValue.slice(amountIndex),
					)
				} else {
					valueEl.textContent = formattedValue
				}
			}
		}
	} catch (error) {
		console.warn('Failed to load collection value', error)
		for (const valueEl of collectionValueEl.querySelectorAll<HTMLElement>(
			'[data-collection-value]',
		)) {
			valueEl.textContent = '—'
		}
	} finally {
		collectionValueEl.classList.remove('is-loading')
		collectionValueEl.setAttribute('aria-busy', 'false')
	}
}

/** Compacts large currency amounts to a short K suffix label. */
function compactCollectionValue(value: string): string {
	const match = value.match(/[\d][\d,]*(?:\.\d+)?/)
	if (!match) return value

	const amount = Number(match[0].replaceAll(',', ''))
	if (!Number.isFinite(amount) || Math.abs(amount) < 1000) return value

	const compactAmount = `${(amount / 1000).toFixed(1).replace(/\.0$/, '')}K`
	return `${value.slice(0, match.index)}${compactAmount}${value.slice((match.index ?? 0) + match[0].length)}`
}
